import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { X, Building2, MapPin, Ruler, User, Phone, Eye, EyeOff, AlertCircle, Lock, BadgeCheck } from "lucide-react";

const FLOOR_LABELS = { GF: "Ground Floor", FF: "First Floor" };
const WING_LABELS  = { R: "Block A", L: "Block B", "": "" };

const STATUS_META = {
  occupied: { bg: "bg-blue-50 dark:bg-blue-950/30",     text: "text-blue-700 dark:text-blue-300",     border: "border-blue-200 dark:border-blue-900",     dot: "bg-blue-600",     label: "Occupied" },
  claimed:  { bg: "bg-emerald-50 dark:bg-emerald-950/30", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-200 dark:border-emerald-900", dot: "bg-emerald-600",  label: "Claimed"  },
  vacant:   { bg: "bg-amber-50 dark:bg-amber-950/30",   text: "text-amber-700 dark:text-amber-300",   border: "border-amber-200 dark:border-amber-900",   dot: "bg-amber-600",   label: "Vacant"   },
  missing:  { bg: "bg-zinc-50 dark:bg-zinc-950/30",     text: "text-zinc-600 dark:text-zinc-400",     border: "border-zinc-200 dark:border-zinc-800",     dot: "bg-zinc-500",    label: "Missing"  },
};

export default function UnitDetailDrawer({ unitId, onClose }) {
  const { user } = useAuth();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const backdropRef = useRef(null);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!unitId) return;
    setLoading(true);
    const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
    axios.get(`${API}/units/${unitId}`, { headers })
      .then((r) => setUnit(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [unitId, user]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!unitId) return null;

  const floorLabel = unit
    ? `${FLOOR_LABELS[unit.floor] || unit.floor}${WING_LABELS[unit.wing] ? " · " + WING_LABELS[unit.wing] : ""}`
    : "";
  const isOwn   = user?.role === "owner";
  const isAdmin = user?.role === "admin";
  const canSeeFullDetails = isOwn || isAdmin;
  const status  = unit?.directory_status || "missing";
  const meta    = STATUS_META[status] || STATUS_META.missing;

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        style={{ opacity: mounted ? 1 : 0, transition: "opacity 240ms ease" }}
        onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
        data-testid="drawer-backdrop"
      />
      {/* Drawer panel */}
      <aside
        className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] max-w-full bg-white dark:bg-[#1A1D24] shadow-2xl flex flex-col overflow-hidden border-l border-[#E5E5E5] dark:border-[#272A30]"
        style={{
          transform: mounted ? "translateX(0)" : "translateX(100%)",
          transition: "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
          paddingBottom: "env(safe-area-inset-bottom)",
          paddingTop: "env(safe-area-inset-top)",
        }}
        data-testid="unit-detail-drawer"
        role="dialog"
        aria-label="Unit detail"
      >
        {/* Gold rail */}
        <div className="h-1 w-full bg-gradient-to-r from-[#9C7C54] via-[#C0A062] to-[#9C7C54] flex-shrink-0" />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-3 flex-shrink-0">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C7C54] font-semibold mb-1">Unit</div>
            <h2
              className="font-heading text-3xl font-light text-[#1A1A1A] dark:text-[#EAEAEA]"
              data-testid="drawer-unit-id"
            >
              {unit?.unit_id || "…"}
            </h2>
            {unit && (
              <div className="mt-1 text-[11px] text-[#737373]">{floorLabel}</div>
            )}
          </div>
          <button
            data-testid="drawer-close-btn"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-[#737373] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA] transition-colors rounded-sm hover:bg-[#F5F3F0] dark:hover:bg-[#272A30]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#9C7C54] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : unit ? (
          <>
            {/* Status pill */}
            <div className="px-6 pb-4 flex-shrink-0">
              <span
                data-testid="drawer-status"
                className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border ${meta.bg} ${meta.text} ${meta.border}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                {meta.label}
                {status === "claimed" && <BadgeCheck size={12} />}
              </span>
            </div>

            {/* Details */}
            <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#F9F8F6] dark:bg-[#0F1115] p-4 rounded-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <MapPin size={12} className="text-[#9C7C54]" />
                    <span className="text-[10px] tracking-[0.15em] uppercase text-[#737373]">Floor</span>
                  </div>
                  <div className="text-sm font-medium text-[#1A1A1A] dark:text-[#EAEAEA]" data-testid="drawer-floor">
                    {floorLabel}
                  </div>
                </div>
                <div className="bg-[#F9F8F6] dark:bg-[#0F1115] p-4 rounded-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Ruler size={12} className="text-[#9C7C54]" />
                    <span className="text-[10px] tracking-[0.15em] uppercase text-[#737373]">Size</span>
                  </div>
                  <div className="text-sm font-medium text-[#1A1A1A] dark:text-[#EAEAEA]" data-testid="drawer-size">
                    {unit.size_sqft > 0 ? `${unit.size_sqft.toFixed(2)} sq ft` : "—"}
                  </div>
                </div>
                <div className="col-span-2 bg-[#F9F8F6] dark:bg-[#0F1115] p-4 rounded-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Building2 size={12} className="text-[#9C7C54]" />
                    <span className="text-[10px] tracking-[0.15em] uppercase text-[#737373]">Occupancy (raw)</span>
                  </div>
                  <div className="text-sm font-medium text-[#1A1A1A] dark:text-[#EAEAEA]" data-testid="drawer-occupancy">
                    {unit.occupancy_status.charAt(0).toUpperCase() + unit.occupancy_status.slice(1)}
                  </div>
                </div>
              </div>

              {/* Owner block */}
              {unit.owners && unit.owners.length > 0 ? (
                <div className="border-t border-[#E5E5E5] dark:border-[#272A30] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#737373]">
                      {unit.owners.length > 1 ? "Owners" : "Owner"}
                    </span>
                    {!canSeeFullDetails && (
                      <div className="flex items-center gap-1 text-[10px] text-[#737373]">
                        <Lock size={10} />
                        <span>Details masked for privacy</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {unit.owners.map((o, i) => (
                      <div
                        key={o.owner_id}
                        className="flex items-start gap-3 p-3 rounded-sm bg-[#F9F8F6] dark:bg-[#0F1115]"
                      >
                        <div className="w-9 h-9 rounded-full bg-[#F5F3F0] dark:bg-[#272A30] flex items-center justify-center flex-shrink-0">
                          <User size={15} className="text-[#9C7C54]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              data-testid={`drawer-owner-name-${i}`}
                              className="text-sm font-medium text-[#1A1A1A] dark:text-[#EAEAEA]"
                            >
                              {o.name}
                            </span>
                            {o.is_verified && (
                              <span
                                className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                                data-testid={`drawer-owner-verified-${i}`}
                              >
                                <BadgeCheck size={9} /> Verified
                              </span>
                            )}
                            {o.is_masked
                              ? <EyeOff size={12} className="text-[#737373]" />
                              : <Eye    size={12} className="text-emerald-500" />}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <Phone size={11} className="text-[#9C7C54]" />
                            <span
                              data-testid={`drawer-owner-phone-${i}`}
                              className={`text-xs font-mono text-[#737373] ${o.is_masked ? "tracking-widest" : ""}`}
                            >
                              {o.phone}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="border-t border-[#E5E5E5] dark:border-[#272A30] pt-4">
                  <div className="flex items-center gap-2 text-[#737373]">
                    <AlertCircle size={14} />
                    <span className="text-sm">No owner record linked</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex-shrink-0 border-t border-[#E5E5E5] dark:border-[#272A30] px-6 py-4 flex items-center gap-3 bg-white dark:bg-[#1A1D24]">
              {!canSeeFullDetails ? (
                <a
                  href="/owner/login"
                  className="flex-1 text-center py-2.5 bg-[#9C7C54] text-white text-sm tracking-wide hover:bg-[#7a5f3e] transition-colors rounded-sm"
                  data-testid="drawer-owner-login-link"
                >
                  Owner? Sign In to View Full Details
                </a>
              ) : isOwn ? (
                <a
                  href="/owner/dashboard"
                  className="flex-1 text-center py-2.5 bg-[#9C7C54] text-white text-sm tracking-wide hover:bg-[#7a5f3e] transition-colors rounded-sm"
                  data-testid="drawer-owner-dashboard-link"
                >
                  Go to My Dashboard
                </a>
              ) : (
                <div className="flex-1 text-[11px] text-[#737373]">Signed in as admin — full details visible</div>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2.5 border border-[#E5E5E5] dark:border-[#272A30] text-sm text-[#737373] hover:border-[#9C7C54] hover:text-[#9C7C54] transition-colors rounded-sm"
                data-testid="drawer-close-bottom-btn"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-[#737373] text-sm">Unit not found</div>
        )}
      </aside>
    </>
  );
}
